import React, { useState, useMemo } from 'react';
import { IconButton, Tooltip, Paper, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  GridValueFormatterParams,
  GridRenderCellParams,
  GridValueGetterParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { User as PersonIcon } from 'react-feather';
import { startCase } from 'lodash';
import moment from 'moment';
import { FullScreen } from '@pv/common/components';
import { formatPercentageRate, moneyPrint } from '@pv/common/utils';
import { ADMIN_VENUES_QUERY } from './graphql';
import { AdminReport, SearchBar } from '../reports';

const formatMarketplaceRank = (rank: number) => {
  switch (rank) {
    case -2:
      return 'Removed';
    case -1:
      return 'Not Ready';
    case 0:
      return 'Pending Review';
    case 1:
      return 'Approved';
    default:
      return '-';
  }
};
export const VenuesTable = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');

  const additionalQueryVariables = useMemo(
    () => ({
      search: search || undefined,
    }),
    [search]
  );

  const renderSearchBar = () => (
    <SearchBar
      onSearchChange={setSearch}
      placeholder="Search venues by name..."
    />
  );

  const allColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: true,
    },
    {
      field: 'organizationName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.organization?.name,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component={RouterLink}
          to={`/admin/organizations/${params.row.organization.id}/info`}
          underline="hover"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {params.row.organization.name}
        </Link>
      ),
    },
    {
      field: 'connectedStripe',
      headerName: 'Connected Stripe',
      type: 'boolean',
      valueGetter: (params: GridValueGetterParams) =>
        !!params.row.stripeAccount?.id,
      sortable: false,
    },
    {
      field: 'connectedSquare',
      headerName: 'Connected Square',
      type: 'boolean',
      valueGetter: (params: GridValueGetterParams) =>
        !!params.row.squareAccount?.id && !!params.row.squareLocationId,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component={RouterLink}
          to={`/admin/venues/${params.row.id}/info`}
          underline="hover"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {params.row.name}
        </Link>
      ),
    },
    {
      field: 'basicFeaturesEnabled',
      headerName: 'Basic Features',
      width: 130,
      type: 'boolean',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.featureScope?.basicFeaturesEnabled,
    },
    {
      field: 'proFeaturesEnabled',
      headerName: 'Pro Features',
      width: 130,
      type: 'boolean',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.featureScope?.proFeaturesEnabled,
    },
    {
      field: 'premiumFeaturesEnabled',
      headerName: 'Premium Features',
      width: 150,
      type: 'boolean',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.featureScope?.premiumFeaturesEnabled,
    },
    {
      field: 'enterpriseFeaturesEnabled',
      headerName: 'Enterprise Features',
      width: 150,
      type: 'boolean',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.featureScope?.enterpriseFeaturesEnabled,
    },
    {
      field: 'marketplaceRank',
      headerName: 'Marketplace Rank',
      width: 180,
      type: 'string',
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) =>
        formatMarketplaceRank(params.value),
    },
    {
      field: 'websiteUrl',
      headerName: 'Website URL',
      flex: 1,
      minWidth: 250,
      sortable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
      sortable: true,
    },
    {
      field: 'tier',
      headerName: 'Plan Tier',
      width: 130,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        startCase(params.row.subscription?.tier),
    },
    {
      field: 'billingInterval',
      headerName: 'Billing Interval',
      width: 130,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        startCase(params.row.subscription?.billingInterval),
    },
    {
      field: 'monthlyRate',
      headerName: 'Monthly Rate',
      width: 120,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        moneyPrint(params.row.subscription?.monthlyRate / 100 || 0),
    },
    {
      field: 'cardApplicationFeeRate',
      headerName: 'Card Processing Rate',
      width: 180,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        formatPercentageRate(
          params.row.subscription?.cardApplicationFeeRate || 0
        ),
    },
    {
      field: 'achApplicationFeeRate',
      headerName: 'ACH Processing Rate',
      width: 180,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        formatPercentageRate(
          params.row.subscription?.achApplicationFeeRate || 0
        ),
    },
    {
      field: 'stripeSubscriptionId',
      headerName: 'Stripe Subscription ID',
      width: 180,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.subscription?.stripeSubscriptionId,
    },
    {
      field: 'achEnabled',
      headerName: 'ACH Enabled',
      width: 120,
      type: 'boolean',
      sortable: true,
      valueGetter: (params: GridValueGetterParams) => params.row.achEnabled,
    },
    {
      field: 'eventsCount',
      headerName: 'Events',
      width: 100,
      type: 'number',
      sortable: false,
    },
    {
      field: 'spacesCount',
      headerName: 'Spaces',
      width: 100,
      type: 'number',
      sortable: false,
    },
    {
      field: 'customerStartedAt',
      headerName: 'Customer Start Date',
      width: 160,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value && moment(params.value).format('MM/DD/YYYY'),
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 200,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.address?.formattedAddress,
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 120,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Login as User">
          <IconButton
            size="small"
            href={params.row.users?.[0]?.magicLoginLink}
            color="primary"
          >
            <PersonIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%' }}>
            <AdminReport
              reportName="adminVenues"
              columns={allColumns}
              title="Venues"
              toolbar={true}
              query={ADMIN_VENUES_QUERY}
              additionalQueryVariables={additionalQueryVariables}
              renderCustomSearchBar={renderSearchBar}
              onRowClick={(params: GridRowParams) => {
                navigate(`/admin/venues/${params.row.id}/info`);
              }}
            />
          </Paper>
        )}
      </FullScreen>
    </>
  );
};

export default VenuesTable;
